import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
    AtomicStatusDocument,
    AtomicStatusType,
    DocumentsDocument,
    DocumentUrlDocument,
    SyncAtomicUserDocument,
    SyncRecommendedRiskScoreByHorizonDocument,
} from '../../graphql/generated';
import {
    AtomicStatusTypeInfo,
    RecommendedRiskScoreByHorizon,
} from '../../types';
import { sortBy } from '../../util/arrays';
import { dt } from '../../util/dateTime';
import { useCurrentUser } from '../user';

/**
 * Hook to check if Atomic operations (new investments, deposits, withdrawals) are enabled.
 * When disabled, users can still view their existing portfolio and holdings,
 * but cannot perform any new investment operations.
 *
 * This is controlled by a hardcoded flag - set to false to disable Atomic operations.
 */
export const useAtomicEnabled = (): {
    isAtomicEnabled: boolean;
    atomicDisabledMessage: string;
} => {
    // Hardcoded to match the backend flag in api/src/services/atomic/index.ts
    const isAtomicEnabled = false;

    return {
        isAtomicEnabled,
        atomicDisabledMessage: isAtomicEnabled
            ? ''
            : 'Investment operations are temporarily unavailable. You can still view your portfolio and holdings.',
    };
};

export interface AtomicStatus {
    status: AtomicStatusType;
    statusInfo: AtomicStatusTypeInfo;
}

export const useAtomicStatus = (): AtomicStatus | undefined => {
    const { data, loading, error } = useQuery(AtomicStatusDocument);
    if (!data || loading || error) return;
    return data.atomicStatus as AtomicStatus;
};

export const useSyncAtomicUser = (): (() => Promise<
    AtomicStatus | undefined
>) => {
    const [syncAtomicUser] = useMutation(SyncAtomicUserDocument);
    return useCallback(async () => {
        const res = await syncAtomicUser();
        const atomicStatus = res?.data?.syncAtomicUser?.query?.atomicStatus;
        if (!atomicStatus) return;
        return atomicStatus as AtomicStatus;
    }, [syncAtomicUser]);
};

export const useSyncRecommendedRiskScoreByHorizon = (): (() => Promise<
    RecommendedRiskScoreByHorizon | null | undefined
>) => {
    const [syncRecommendedRiskScoreByHorizon] = useMutation(
        SyncRecommendedRiskScoreByHorizonDocument
    );
    return useCallback(async () => {
        const res = await syncRecommendedRiskScoreByHorizon();
        return res?.data?.syncRecommendedRiskScoreByHorizon?.query
            ?.currentUserProfile?.recommendedRiskScoreByHorizon;
    }, [syncRecommendedRiskScoreByHorizon]);
};

export const useMaybeSyncRecommendedRiskScores = ({ maxRetries = 5 } = {}) => {
    const { userInfo } = useCurrentUser();
    const areRiskScoresSynced = !!userInfo.recommendedRiskScoreByHorizon;
    const [syncRiskScores] = useMutation(
        SyncRecommendedRiskScoreByHorizonDocument
    );

    const maybeSyncRiskScores = useCallback(
        (areSynced: boolean, tries = 0) => {
            if (tries >= maxRetries) return;
            if (!areSynced) {
                setTimeout(
                    () => {
                        syncRiskScores({
                            onError: () => {
                                maybeSyncRiskScores(areSynced, tries + 1);
                            },
                        });
                    },
                    1000 * Math.pow(2, tries) // Exponential backoff: 1s, 2s, 4s, 8s, 16s
                );
            }
        },
        [syncRiskScores, maxRetries]
    );

    useEffect(() => {
        maybeSyncRiskScores(areRiskScoresSynced);
    }, [areRiskScoresSynced, maybeSyncRiskScores]);
};

export const useIsJointAccountConsentRequired = () => {
    const { plentyJointAccountStatus } = useCurrentUser();

    return (
        plentyJointAccountStatus === 'ELIGIBLE' ||
        plentyJointAccountStatus === 'PARTNER_CONSENTED'
    );
};

type Document = {
    id: string;
    title: string;
    date: string;
};

export const useInvestmentDocuments = ({
    onError,
    onQueryDocumentUrlSuccess,
}: {
    onError: VoidFunction;
    onQueryDocumentUrlSuccess: (documentUrl: string) => void;
}) => {
    const { data, loading } = useQuery(DocumentsDocument);
    const [queryDocumentUrl] = useLazyQuery(DocumentUrlDocument);
    const [downloadingDocumentId, setDownloadingDocumentId] = useState<
        string | null
    >(null);

    const downloadDocument = async (documentId: string) => {
        if (downloadingDocumentId) return;

        setDownloadingDocumentId(documentId);

        const { data, error } = await queryDocumentUrl({
            variables: {
                id: documentId,
            },
        });

        if (!data || error || !data.documentUrl) {
            setDownloadingDocumentId(null);
            onError();
            return;
        }

        setDownloadingDocumentId(null);
        onQueryDocumentUrlSuccess(data.documentUrl);
    };

    // TODO: Consider moving this logic to the server
    const documents = sortBy(
        data?.documents?.map((d) => ({
            id: d.id,
            date:
                // The dates returned from the API are not accurate, so:
                // - For tax-related documents, show only the year
                // - For all other documents, show the year and month
                [
                    'Tax Statement',
                    'Notification 1099',
                    'Tax Form 1099',
                ].includes(d.title)
                    ? d.date.slice(0, 4)
                    : d.date.slice(0, 7),
            title: d.title,
        })) ?? [],
        // Sort year-only values as end-of-year
        (d) => `${d.date.length === 4 ? d.date + '-99' : d.date}_${d.title}`,
        'DESC'
    );

    return {
        documents,
        downloadDocument,
        downloadingDocumentId,
        loading,
    };
};

export const useCurrent1099Document = (documents: Document[]) => {
    // Only show callout if user has opened an Atomic account
    const atomicStatus = useAtomicStatus();
    if (atomicStatus?.status !== 'ACCOUNT_COMPLETE') {
        return {
            showCurrent1099Callout: false,
            current1099: null,
        };
    }

    // Current 1099 is
    // 1. Date is the previous year
    // 2. Title is "Tax Form 1099"
    const current1099 = documents.find(
        (d) =>
            d.date.slice(0, 4) === dt().subtract(1, 'year').format('YYYY') &&
            (d.title === 'Tax Form 1099' || d.title === 'Tax Statement')
    );
    // Show current 1099 if
    // 1. current 1099 exists
    // 2. current month is before June of this calendar year
    const showCurrent1099Callout = dt().month() < 6;

    return {
        current1099,
        showCurrent1099Callout,
    };
};
